import { buildProps, definePropType } from '@ll-plus/utils'

import type { InputProps as AntInputProps } from 'ant-design-vue'
import type { ExtractPropTypes } from 'vue'

import type InputSearch from './input-search.vue'
import type Input from './input.vue'
import type PasswordInput from './password-input.vue'

interface Rule {
  label: string
  regex: string
}

// 输入框
export const inputProps = buildProps({
  /**
   * @description input的defaultValue
   */
  defaultValue: {
    type: definePropType<AntInputProps['defaultValue']>(String),
    default: '',
  },
  /**
   * @description input的value
   */
  value: {
    type: definePropType<AntInputProps['value']>(String),
  },
  /**
   * @description 带有前缀图标的 input
   */
  prefixIcon: {
    type: String,
  },
  /**
   * @description 带有后缀图标的 input
   */
  suffixIcon: {
    type: String,
  },
  /**
   * @description 是否显示密码框
   */
  showPassword: {
    type: Boolean,
  },
  /**
   * @description 是否点击清除图标删除内容
   */
  allowClear: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否禁用状态，默认为 false
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * @description 是否只读状态，默认为 false ,allowClear需独立设置
   */
  readonly: {
    type: Boolean,
    default: false,
  },
  /**
   * @description 是否灰色查询样式
   */
  isGray: {
    type: Boolean,
    default: false,
  },

  // 最大输入数
  maxlength: {
    type: Number,
  },
} as const)

// 查询输入框
export const inputSearchProps = buildProps({
  /**
   * @description input的defaultValue
   */
  defaultValue: {
    type: definePropType<AntInputProps['defaultValue']>(String),
    default: '',
  },
  /**
   * @description input的value
   */
  value: {
    type: definePropType<AntInputProps['value']>(String),
  },
  /**
   * @description 是否点击清除图标删除内容
   */
  allowClear: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 是否禁用状态，默认为 false
   */
  disabled: {
    type: Boolean,
    default: false,
  },
} as const)

// 密码输入框
export const passwordInputProps = buildProps({
  // 是否灰色样式
  isGray: {
    type: Boolean,
    default: true,
  },
  // 最大输入限制
  maxlength: Number,

  // 值
  value: {
    type: String,
    default: '',
  },
  // 校验正则
  rules: {
    type: definePropType<Rule[]>(Array),
    default: () => [],
  },
  // 是否显示密码强度
  pwdStrength: {
    type: Boolean,
    default: true,
  },
  // 弱强度颜色
  weakColor: {
    type: String,
    default: 'red',
  },
  // 中等强度颜色
  mediumColor: {
    type: String,
    default: 'yellow',
  },
  // 非常强度颜色
  strongColor: {
    type: String,
    default: 'green',
  },
  // 提示标题
  tipTitle: {
    type: String,
    default: '建议',
  },
  // 触发方式 点击切换 / 按下显示
  trigger: {
    type: String,
    default: 'hold',
    values: ['click', 'hold'],
  },
  // 密码是否可见
  visible: Boolean,
} as const)

export const passwordInputEmits = {
  change: (value: string) => !!value || typeof value === 'string',
  'update:value': (value: string) => !!value || typeof value === 'string',
  'update:visible': (visible: boolean) => typeof visible === 'boolean',
  state: (value: boolean) => typeof value === 'boolean',
} as const

export type InputProps = ExtractPropTypes<typeof inputProps> // 输入框
export type InputSearchProps = ExtractPropTypes<typeof inputSearchProps> // 查询输入框
export type PasswordInputProps = ExtractPropTypes<typeof passwordInputProps> // 密码输入框

export type InputValue = AntInputProps['value']

export type PasswordInputEmits = typeof passwordInputEmits // 密码输入框

export type InputInstance = InstanceType<typeof Input> // 输入框
export type InputSearchInstance = InstanceType<typeof InputSearch> // 查询输入框
export type PasswordInputInstance = InstanceType<typeof PasswordInput> // 密码输入框
